import React from "react";
import { FiRefreshCw } from "react-icons/fi";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import Loader from "../../components/partials/components/loader";

/* ─── Status-aware colour map ─────────────────────────────────────────────── */
const STATUS_COLORS = {
  available: "#10b981",
  Available: "#10b981",
  busy:      "#f43f5e",
  Busy:      "#f43f5e",
  offline:   "#94a3b8",
  Offline:   "#94a3b8",
};

const FALLBACK_COLORS = ["#10b981", "#f43f5e", "#94a3b8", "#6366f1", "#f97316", "#0ea5e9"];

const getColor = (entry, index) =>
  STATUS_COLORS[entry.status] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];

/* ─── Glassmorphism tooltip ───────────────────────────────────────────────── */
const DashboardChartTooltip = ({ active, payload, label }) => {
  const isDark = document.body.classList.contains("dark");

  if (!active || !payload || !payload.length) return null;

  return (
    <div
      style={{
        background:   isDark ? "rgba(17,24,39,0.92)" : "rgba(255,255,255,0.92)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border:       isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid rgba(15,23,42,0.10)",
        borderRadius: 12,
        padding:      "10px 14px",
        boxShadow:    isDark
          ? "0 12px 32px rgba(2,6,23,0.45)"
          : "0 12px 32px rgba(15,23,42,0.12)",
        minWidth:     100,
      }}
    >
      {label && (
        <div
          style={{
            fontWeight: 700,
            fontSize: 12,
            marginBottom: 6,
            color: isDark ? "#f1f5f9" : "#0f172a",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          }}
        >
          {label}
        </div>
      )}
      {payload.map((entry, i) => (
        <div
          key={`${entry.name}-${i}`}
          style={{
            display:     "flex",
            alignItems:  "center",
            gap:         8,
            fontSize:    13,
            color:       isDark ? "#e2e8f0" : "#1e293b",
            marginTop:   i > 0 ? 4 : 0,
          }}
        >
          <span
            style={{
              width:        10,
              height:       10,
              borderRadius: "50%",
              background:   entry.color || entry.fill || "#6366f1",
              flexShrink:   0,
              display:      "inline-block",
            }}
          />
          <span style={{ fontWeight: 600 }}>{entry.name}:</span>
          <span>{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ─── Custom bar shape with rounded top ──────────────────────────────────── */
const RoundedBar = (props) => {
  const { x, y, width, height, fill } = props;
  if (!height || height <= 0) return null;
  const r = Math.min(6, width / 2);
  return (
    <path
      d={`M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} Z`}
      fill={fill}
    />
  );
};

/* ─── Custom legend ───────────────────────────────────────────────────────── */
const CustomLegend = ({ data }) => (
  <div
    style={{
      display:        "flex",
      flexWrap:       "wrap",
      gap:            "6px 16px",
      justifyContent: "center",
      marginTop:      8,
    }}
  >
    {data.map((entry, i) => (
      <span
        key={entry.status}
        style={{
          display:    "flex",
          alignItems: "center",
          gap:        6,
          fontSize:   12,
          color:      "var(--dash-text-soft)",
          fontWeight: 500,
        }}
      >
        <span
          style={{
            width:        10,
            height:       10,
            borderRadius: "50%",
            background:   getColor(entry, i),
            display:      "inline-block",
          }}
        />
        {entry.status}
      </span>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════
   AgentStatusDistribution
═══════════════════════════════════════════════════════════════════════════ */
const AgentStatusDistribution = ({ graphData, onRefresh, loading }) => {
  const safeData = Array.isArray(graphData) ? graphData : [];

  const totalAgents = safeData.reduce((sum, d) => sum + (d.agents || 0), 0);

  if (loading) {
    return <Loader />
  }

  /* ── Card shell ─────────────────────────────────────────────────────────── */
  return (
    <div
      className="dashboard-chart-card"
      style={{
        background:   "var(--dash-surface)",
        border:       "1px solid var(--dash-border)",
        borderRadius: "var(--dash-radius)",
        boxShadow:    "var(--dash-shadow)",
        overflow:     "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display:        "flex",
          alignItems:     "flex-start",
          justifyContent: "space-between",
          padding:        "18px 22px 14px",
          borderBottom:   "1px solid var(--dash-border)",
        }}
      >
        <div>
          <div
            className="dashboard-section-title"
            style={{
              fontWeight:    700,
              fontSize:      16,
              color:         "var(--dash-text)",
              letterSpacing: "-0.01em",
              marginBottom:  2,
            }}
          >
            Agent Status Distribution
          </div>
          <div
            style={{
              fontSize:   12,
              color:      "var(--dash-text-soft)",
              fontWeight: 500,
            }}
          >
            Live agent availability overview
          </div>
        </div>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            alignSelf: "flex-start",
          }}
        >
          <button
            type="button"
            onClick={onRefresh}
            style={{
              border: "1px solid var(--dash-border)",
              background: "var(--dash-surface-alt)",
              color: "var(--dash-text-soft)",
              borderRadius: 999,
              width: 30,
              height: 30,
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            title="Refresh Agent Status Distribution"
            aria-label="Refresh Agent Status Distribution"
          >
            <FiRefreshCw size={13} />
          </button>
          <div
            style={{
              background:   "var(--dash-primary-soft)",
              color:        "var(--dash-primary)",
              borderRadius: 999,
              padding:      "4px 12px",
              fontSize:     12,
              fontWeight:   700,
              letterSpacing: "0.02em",
              whiteSpace:   "nowrap",
            }}
          >
            {totalAgents} agents
          </div>
        </div>
      </div>

      {/* Body — two-panel layout */}
      <div
        style={{
          display:   "flex",
          flexWrap:  "wrap",
          gap:       0,
          padding:   "16px 8px 20px",
        }}
      >
        {/* ── Bar chart ── */}
        <div style={{ flex: "1 1 280px", minWidth: 0, padding: "0 12px" }}>
          <div
            style={{
              fontSize:   11,
              fontWeight: 700,
              color:      "var(--dash-text-soft)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 8,
              paddingLeft: 4,
            }}
          >
            By Status — Bar
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={safeData}
              margin={{ top: 10, right: 16, left: -10, bottom: 4 }}
              barCategoryGap="35%"
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--dash-border)"
                vertical={false}
              />
              <XAxis
                dataKey="status"
                tick={{ fontSize: 12, fill: "var(--dash-text-soft)", fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--dash-text-soft)" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<DashboardChartTooltip />}
                cursor={{ fill: "var(--dash-primary-soft)" }}
              />
              <Bar dataKey="agents" name="Agents" shape={<RoundedBar />}>
                {safeData.map((entry, index) => (
                  <Cell key={`bar-${index}`} fill={getColor(entry, index)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <CustomLegend data={safeData} />
        </div>

        {/* Vertical divider — visible on larger screens */}
        <div
          style={{
            width:      1,
            background: "var(--dash-border)",
            margin:     "10px 4px",
            flexShrink: 0,
            alignSelf:  "stretch",
          }}
        />

        {/* ── Donut chart ── */}
        <div style={{ flex: "1 1 280px", minWidth: 0, padding: "0 12px" }}>
          <div
            style={{
              fontSize:   11,
              fontWeight: 700,
              color:      "var(--dash-text-soft)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 8,
              paddingLeft: 4,
            }}
          >
            Distribution — Donut
          </div>
          <div style={{ position: "relative" }}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={safeData}
                  dataKey="agents"
                  nameKey="status"
                  cx="50%"
                  cy="48%"
                  innerRadius={72}
                  outerRadius={110}
                  paddingAngle={3}
                  startAngle={90}
                  endAngle={-270}
                >
                  {safeData.map((entry, index) => (
                    <Cell
                      key={`pie-${index}`}
                      fill={getColor(entry, index)}
                      stroke="transparent"
                    />
                  ))}
                </Pie>
                <Tooltip content={<DashboardChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            {/* Centre label */}
            <div
              style={{
                position:   "absolute",
                top:        "50%",
                left:       "50%",
                transform:  "translate(-50%, -58%)",
                textAlign:  "center",
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  fontSize:   28,
                  fontWeight: 800,
                  color:      "var(--dash-text)",
                  lineHeight: 1,
                }}
              >
                {totalAgents}
              </div>
              <div
                style={{
                  fontSize:   11,
                  color:      "var(--dash-text-soft)",
                  fontWeight: 600,
                  marginTop:  2,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Total
              </div>
            </div>
          </div>
          <CustomLegend data={safeData} />
        </div>
      </div>
    </div>
  );
};

export default AgentStatusDistribution;
